import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, Upload, ExternalLink, FileText, Trash2, Receipt } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MIME_WHITELIST = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const TENANT_ALLOWED_TYPES = [
  { value: 'DepositProtection', label: 'Deposit Protection Certificate' },
  { value: 'PrescribedInfo', label: 'Prescribed Information' },
  { value: 'TenantID', label: 'Tenant ID Document' },
  { value: 'RightToRentDoc', label: 'Right to Rent Document' },
  { value: 'ProofOfIncome', label: 'Proof of Income' },
  { value: 'EmploymentReference', label: 'Employment Reference' },
  { value: 'PreviousLandlordReference', label: 'Previous Landlord Reference' },
  { value: 'GuarantorDocument', label: 'Guarantor Document' },
  { value: 'TenantInsurance', label: 'Tenant Insurance' },
  { value: 'TenantNotice', label: 'Tenant Notice' },
  { value: 'TenantMaintenancePhoto', label: 'Tenant Maintenance Photo' },
  { value: 'DepositDisputeEvidence', label: 'Deposit Dispute Evidence' },
];

const TENANT_LABELS = TENANT_ALLOWED_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const getTenantLabel = (value) => TENANT_LABELS[value] || value;

const getDownloadUrl = (doc) => {
  if (!doc?.download_url) return null;
  return API_URL ? `${API_URL}${doc.download_url}` : doc.download_url;
};

export const TenantDocumentsPanel = ({
  rentHistory = [],
  onViewReceipt,
  notifications = [],
  highlightedDocumentId = null,
  onMarkSharedDocumentViewed,
}) => {
  const { user, isTenant } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentType, setDocumentType] = useState('');
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pendingDeleteDocument, setPendingDeleteDocument] = useState(null);

  const tenancyId = user?.tenancy_id;

  const { myDocuments, sharedDocuments } = useMemo(() => {
    const mine = [];
    const shared = [];
    documents.forEach((doc) => {
      if (doc.created_by_user_id === user?.id || doc.uploaded_by === 'tenant') {
        mine.push(doc);
      } else if (doc.visibility === 'tenant_visible') {
        shared.push(doc);
      }
    });
    return { myDocuments: mine, sharedDocuments: shared };
  }, [documents, user?.id]);

  const unreadSharedDocumentNotificationIdsByDocumentId = useMemo(() => {
    const map = new Map();
    notifications
      .filter((n) => n.type === 'document_shared' && !n.document_viewed && n.document_id)
      .forEach((n) => {
        const list = map.get(n.document_id) || [];
        list.push(n.id);
        map.set(n.document_id, list);
      });
    return map;
  }, [notifications]);

  const handleViewSharedDocument = (doc) => {
    const notifIds = unreadSharedDocumentNotificationIdsByDocumentId.get(doc.id) || [];
    if (notifIds.length > 0) {
      onMarkSharedDocumentViewed?.(doc.id);
    }
  };

  useEffect(() => {
    if (!highlightedDocumentId || loading) return;
    const element = document.getElementById(`tenant-document-${highlightedDocumentId}`);
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [highlightedDocumentId, loading, documents]);

  useEffect(() => {
    if (!isTenant()) return;
    if (!tenancyId) {
      setLoading(false);
      return;
    }

    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/tenancy/${tenancyId}/documents`, {
          withCredentials: true,
        });
        const docsList = Array.isArray(response.data?.documents) ? response.data.documents : [];
        setDocuments(docsList);
      } catch (error) {
        const errorMsg = error.response?.data?.detail || 'Failed to load documents';
        toast.error(errorMsg);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [tenancyId, isTenant]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    if (!MIME_WHITELIST.includes(selectedFile.type)) {
      toast.error('Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG');
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!tenancyId) {
      toast.error('Tenancy is not available for this account');
      return;
    }

    if (!documentType) {
      toast.error('Please select a document type');
      return;
    }

    if (!file) {
      toast.error('Please choose a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const params = {
        document_type: documentType,
        tenancy_id: tenancyId,
      };
      if (issueDate) {
        params.issue_date = issueDate;
      }

      await axios.post(`${API_URL}/api/documents/upload`, formData, {
        params,
        withCredentials: true,
      });

      toast.success('Document uploaded successfully');
      setDocumentType('');
      setFile(null);
      setIssueDate('');

      const response = await axios.get(`${API_URL}/api/tenancy/${tenancyId}/documents`, {
        withCredentials: true,
      });
      const docsList = Array.isArray(response.data?.documents) ? response.data.documents : [];
      setDocuments(docsList);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload document';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = (doc) => {
    setPendingDeleteDocument(doc);
  };

  const confirmDeleteDocument = async () => {
    if (!pendingDeleteDocument?.id) {
      setPendingDeleteDocument(null);
      return;
    }

    try {
      setDeleting(pendingDeleteDocument.id);
      await axios.delete(`${API_URL}/api/documents/${pendingDeleteDocument.id}`);
      setDocuments((prev) => prev.filter((document) => document.id !== pendingDeleteDocument.id));
      toast.success('Document deleted successfully');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete document';
      toast.error(msg);
    } finally {
      setDeleting(null);
      setPendingDeleteDocument(null);
    }
  };

  if (!isTenant()) {
    return null;
  }

  return (
    <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Tenant Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground">
                Upload your documents or view landlord-shared documents for your tenancy.
              </p>
            </div>

            {!tenancyId && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Tenancy information is not available yet. Documents can be uploaded once your tenancy is confirmed.
              </div>
            )}

            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>Document type</Label>
                <Select value={documentType} onValueChange={setDocumentType} disabled={uploading || !tenancyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TENANT_ALLOWED_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Issue date (optional)</Label>
                <input
                  type="date"
                  value={issueDate}
                  onChange={(event) => setIssueDate(event.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                  disabled={uploading || !tenancyId}
                />
              </div>

              <div className="space-y-2">
                <Label>File (max 10MB)</Label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                  disabled={uploading || !tenancyId}
                />
                {file && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <Button type="submit" disabled={uploading || !tenancyId} className="gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload document
              </Button>
            </form>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-royal-600" />
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>My uploaded documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {myDocuments.length > 0 ? (
                  myDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      id={`tenant-document-${doc.id}`}
                      className={`flex items-center justify-between rounded-md border p-4 ${highlightedDocumentId === doc.id ? 'border-royal-500 bg-royal-50' : ''}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{getTenantLabel(doc.document_type)}</p>
                        <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getDownloadUrl(doc) && (
                          <Button asChild variant="ghost" size="sm">
                            <a href={getDownloadUrl(doc)} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </a>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc)}
                          disabled={deleting === doc.id}
                          className="text-destructive hover:text-destructive hover:bg-red-50"
                        >
                          {deleting === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Landlord-shared documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {sharedDocuments.length > 0 ? (
                  sharedDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      id={`tenant-document-${doc.id}`}
                      className={`flex items-center justify-between rounded-md border p-4 ${highlightedDocumentId === doc.id ? 'border-royal-500 bg-royal-50' : ''}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{getTenantLabel(doc.document_type)}</p>
                        <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-royal-100 text-royal-700">Shared</Badge>
                        {(unreadSharedDocumentNotificationIdsByDocumentId.get(doc.id) || []).length > 0 && (
                          <Badge className="bg-blue-100 text-blue-700">New</Badge>
                        )}
                        {getDownloadUrl(doc) && (
                          <Button asChild variant="ghost" size="sm">
                            <a
                              href={getDownloadUrl(doc)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => handleViewSharedDocument(doc)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No landlord-shared documents yet.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Rent Receipts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {rentHistory.length > 0 ? (
                  rentHistory.map((receipt) => (
                    <div key={receipt.id} className="flex items-center justify-between rounded-md border p-4">
                      <div>
                        <p className="font-medium text-sm">£{receipt.amount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">
                          Paid {new Date(receipt.paid_date).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewReceipt?.(receipt.id)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View Receipt
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No receipts available yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      <AlertDialog
        open={Boolean(pendingDeleteDocument)}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteDocument(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{pendingDeleteDocument?.original_filename}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteDocument}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const TenantDocuments = () => {
  useEffect(() => { document.title = 'My Documents - SIYAK'; }, []);
  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4">
        <TenantDocumentsPanel rentHistory={[]} onViewReceipt={() => {}} />
      </div>
    </Layout>
  );
};
